import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { Institution } from '@/../../backend/src/db/schema';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';

export default function UnsetupInstitutionsPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { t } = useTranslation();
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUnsetupInstitutions = async () => {
      if (!tenantId) return;
      try {
        setLoading(true);
        const response = await fetch(apiUrl(`/api/tenants/${tenantId}/institutions/un-setup`));
        if (!response.ok) {
          throw new Error('Failed to fetch un-setup institutions');
        }
        const data = (await response.json()) as Institution[];
        setInstitutions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchUnsetupInstitutions();
  }, [tenantId]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to={`/system/tenants/${tenantId}`}
          className="p-2 hover:bg-white rounded-full text-gray-500 transition-colors border border-transparent hover:border-gray-200 hover:shadow-sm"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('tenant.unsetupInstitutions.title', 'Un-setup Institutions')}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {t(
              'tenant.unsetupInstitutions.description',
              'List of institutions that are not yet setup for this tenant.',
            )}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.unsetupInstitutions.listTitle', 'Un-setup Institutions List')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>{t('common.loading', 'Loading...')}</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tenant.unsetupInstitutions.name', 'Institution Name')}</TableHead>
                  <TableHead>{t('tenant.unsetupInstitutions.type', 'Type')}</TableHead>
                  <TableHead>{t('tenant.unsetupInstitutions.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {institutions.map((institution) => (
                  <TableRow key={institution.id}>
                    <TableCell>{institution.name}</TableCell>
                    <TableCell>{institution.type}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">
                        {t('tenant.unsetupInstitutions.setup', 'Setup')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
